/* eslint-disable @typescript-eslint/no-explicit-any */
/* Step 1 — Select Structure Type. Accessible role="radiogroup" of cards, one
   per persisted SalaryStructureType. In edit mode the type is locked (changing
   it would invalidate a saved structure), shown as a read-only pill. */
import React from "react";
import { FaCheck, FaLock } from "react-icons/fa";
import { StructureType, TYPE_CARDS, TYPE_BADGE } from "./salaryStructure.config";

interface Props {
  value: StructureType;
  onChange: (next: StructureType) => void;
  locked?: boolean;
}

const StructureTypePicker = ({ value, onChange, locked }: Props) => {
  if (locked) {
    const badge = TYPE_BADGE[value];
    return (
      <div className="rounded-[14px] border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <p className="text-sm font-bold text-slate-800 dark:text-white mb-2">Structure Type</p>
        <div className="inline-flex items-center gap-2">
          <FaLock className="text-slate-400 w-3 h-3" />
          <span className={`px-2.5 py-0.5 rounded text-xs font-semibold ${badge.className}`}>{badge.label}</span>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">
          Structure type can&apos;t be changed after creation. Create a new structure to switch types.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[14px] border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
      <p className="text-sm font-bold text-slate-800 dark:text-white mb-3">Select Structure Type</p>
      <div
        role="radiogroup"
        aria-label="Structure type"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
      >
        {TYPE_CARDS.map((card) => {
          const selected = card.type === value;
          return (
            <label
              key={card.type}
              className={`relative flex flex-col cursor-pointer rounded-[12px] border p-4 transition-all focus-within:ring-4 focus-within:ring-blue-300 ${
                selected
                  ? "border-blue-700 bg-[#f5f8ff] dark:bg-blue-900/20 ring-4 ring-blue-700/10"
                  : "border-slate-200 dark:border-gray-600 hover:border-blue-300 bg-white dark:bg-gray-700"
              }`}
            >
              <input
                type="radio"
                name="structureType"
                value={card.type}
                checked={selected}
                onChange={() => onChange(card.type)}
                className="sr-only"
              />
              {selected && (
                <span className="absolute top-3 right-3 inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-700 text-white">
                  <FaCheck className="w-2.5 h-2.5" />
                </span>
              )}
              <span className="text-sm font-bold text-slate-800 dark:text-white pr-6">{card.title}</span>
              <span className="text-[12px] text-slate-500 dark:text-slate-300 mt-1 leading-snug">
                {card.description}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default StructureTypePicker;
